/**
 * Shared helpers for the checkout analytics screens.
 *
 * Everything date-related on these screens is IST, rendered DD-MM-YYYY.
 * Timestamps arrive as UTC ISO strings; day keys from the day-end report are
 * already IST calendar days ("YYYY-MM-DD") and must NOT be shifted again.
 */
import moment from "moment";

export const IST_TZ = "Asia/Kolkata";

export const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

const isDayKey = (d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d);

export const day = (d) => {
  if (!d) return "—";
  if (isDayKey(d)) {
    const [y, m, dd] = d.split("-");
    return `${dd}-${m}-${y}`;
  }
  const parsed = new Date(d);
  if (isNaN(parsed)) return "—";
  // en-GB gives DD/MM/YYYY; swap to hyphens for the house format.
  return parsed
    .toLocaleDateString("en-GB", { timeZone: IST_TZ })
    .replace(/\//g, "-");
};

/** Today in IST as YYYY-MM-DD — "en-CA" is the locale that formats that way. */
export const istToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: IST_TZ });

export const istDaysAgo = (n) =>
  moment(istToday(), "YYYY-MM-DD").subtract(n, "day");

/** "Mon", "Tue" … for an IST day key. Weekday shape is half the story in a
 *  day-end report — weekend dips look like a crash without it. */
export const weekday = (dayKey) =>
  moment(dayKey, "YYYY-MM-DD").format("ddd");

/**
 * Ranges offered on the analytics view. `days: null` means all time.
 *
 * All time is the DEFAULT: the founder's first question is "how are we doing",
 * not "how were we doing last week", and the aggregation over the full
 * collection returns in well under a second.
 */
export const ANALYTICS_RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "3d", label: "Last 3 days", days: 3 },
  { key: "5d", label: "Last 5 days", days: 5 },
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
];

/**
 * Fill calendar gaps so consecutive rows are genuinely consecutive days.
 *
 * The API zero-fills only when it was given an explicit window; on all-time
 * it returns just the days that had activity. Without this, a row labelled
 * "-100% vs previous day" could be comparing against a day three weeks back —
 * which is exactly the misreading this screen has to avoid.
 *
 * Input and output are both newest-first.
 */
export const fillDayGaps = (days, { through } = {}) => {
  if (!days.length) return days;
  const blank = (dayKey) => ({
    day: dayKey,
    uniqueVisitors: 0,
    visits: 0,
    leads: 0,
    purchases: 0,
    callbacks: 0,
    campaigns: [],
  });
  const out = [];
  // Extend forward to `through` (normally today) so "no leads since Tuesday"
  // is visible as empty days rather than as a missing tail.
  let cursor = moment(through || days[0].day, "YYYY-MM-DD");
  const newest = moment(days[0].day, "YYYY-MM-DD");
  while (cursor.isAfter(newest)) {
    out.push(blank(cursor.format("YYYY-MM-DD")));
    cursor.subtract(1, "day");
  }
  days.forEach((d, i) => {
    out.push(d);
    const next = days[i + 1];
    if (!next) return;
    const gap = moment(d.day, "YYYY-MM-DD").diff(
      moment(next.day, "YYYY-MM-DD"),
      "day"
    );
    for (let k = 1; k < gap; k++)
      out.push(blank(moment(d.day, "YYYY-MM-DD").subtract(k, "day").format("YYYY-MM-DD")));
  });
  return out;
};

/** "Today" / "Yesterday" beat a date for the two rows read most often. */
export const relativeDayLabel = (dayKey) => {
  if (dayKey === istToday()) return "Today";
  if (dayKey === istDaysAgo(1).format("YYYY-MM-DD")) return "Yesterday";
  return null;
};

/**
 * The numbers a founder reads per day, in funnel order. Purchases and
 * callbacks are counted INDEPENDENTLY of each other (someone who asked for a
 * callback and then paid is in both) — this answers "what happened that day",
 * not "where does each lead currently sit", which is what the mutually
 * exclusive stage counts on the campaigns table answer.
 */
export const DAY_METRICS = [
  { key: "uniqueVisitors", label: "Visitors" },
  { key: "leads", label: "Leads" },
  { key: "purchases", label: "Purchases" },
  { key: "callbacks", label: "Callbacks" },
];

/**
 * Lead → paid, the same conversion the campaigns table and the ribbon show,
 * so every number on this screen means the same thing.
 *
 * Deliberately NOT purchases-per-visitor: visit tracking only began on
 * 31 Jul 2026 and is still sparse, so a day with 2 recorded visitors and 5
 * purchases rendered "250%" — a real reading off live staging data. Leads
 * are captured for every buyer, so they are the only sound denominator.
 */
export const dayConvPct = (row) => {
  if (!row.leads) return "—";
  return `${((row.purchases / row.leads) * 100).toFixed(1)}%`;
};

/** Percent change, or null when there is no meaningful baseline. */
export const pctChange = (current, previous) => {
  if (previous == null || current == null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

export const sumMetrics = (rows) =>
  rows.reduce(
    (t, r) => ({
      uniqueVisitors: t.uniqueVisitors + (r.uniqueVisitors || 0),
      visits: t.visits + (r.visits || 0),
      leads: t.leads + (r.leads || 0),
      purchases: t.purchases + (r.purchases || 0),
      callbacks: t.callbacks + (r.callbacks || 0),
    }),
    { uniqueVisitors: 0, visits: 0, leads: 0, purchases: 0, callbacks: 0 }
  );

/**
 * Collapse the day-end report to one campaign's own daily series.
 *
 * A campaign (sub-source) can cover several region/version slugs, so every
 * matching slug in a day is summed. Derived from the SAME payload the
 * day-wise table renders, so the two can never disagree.
 */
export const daysForSlugs = (days, slugs, { trim } = {}) => {
  const wanted = new Set((slugs || []).map((s) => `${s.region}|${s.version}`));
  const mapped = days.map((d) => {
    const mine = (d.campaigns || []).filter((c) =>
      wanted.has(`${c.region}|${c.version}`)
    );
    return { day: d.day, ...sumMetrics(mine) };
  });
  if (!trim) return mapped;
  // "All time" means all of THIS campaign's time. A page that launched in
  // April should not open on four months of leading zeroes just because
  // another page was live back then.
  const live = (r) => r.leads || r.purchases || r.callbacks || r.uniqueVisitors;
  const first = mapped.findIndex(live);
  if (first === -1) return [];
  let last = mapped.length - 1;
  while (last > first && !live(mapped[last])) last--;
  return mapped.slice(first, last + 1);
};
